// src/pages/Buses.tsx
import { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useBreakpointValue,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
} from '@chakra-ui/react';
import { createBus, deleteBus, getBuses, updateBus } from '../../../services/travel/busService';
import BusForm from './BusForm';

const cardsBorderRadii = "8px";

const Buses = () => {
    const [buses, setBuses] = useState<any[]>([]);
    const [selectedBus, setSelectedBus] = useState<any>(null);
    const { isOpen, onOpen, onClose } = useDisclosure();

    // Responsive table sizing
    const tableSize = useBreakpointValue({ base: 'xs', md: 'sm' });
    const minTableWidth = useBreakpointValue({ base: '700px', md: 'auto' });

    const fetchBuses = async () => {
        const response = await getBuses();
        setBuses(response.data);
    };

    useEffect(() => {
        fetchBuses();
    }, []);

    const handleCreate = async (data: any) => {
        await createBus(data);
        fetchBuses();
        onClose();
    };

    const handleUpdate = async (data: any) => {
        if (selectedBus) {
            await updateBus(selectedBus._id, data);
            fetchBuses();
            onClose();
        }
    };

    const handleDelete = async (id: string) => {
        await deleteBus(id);
        fetchBuses();
    };

    return (
        <Box borderRadius={cardsBorderRadii} boxShadow="md" bg="white" minH="100vh">
            <Button size={'sm'} color={'#fff'} bg={'#252525'} m={4} onClick={() => { setSelectedBus(null); onOpen(); }}>
                Add Bus
            </Button>

            <Box overflowX="auto">
                <Table
                    variant="simple"
                    size={tableSize}
                    minW={minTableWidth}
                    whiteSpace="nowrap"
                >
                    <Thead>
                        <Tr bg="#fff" borderTop={'1px solid #EDF2F7'} height="50px">
                            <Th px={{ base: 2, md: 4 }}>Company</Th>
                            <Th px={{ base: 2, md: 4 }}>From</Th>
                            <Th px={{ base: 2, md: 4 }}>To</Th>
                            <Th px={{ base: 2, md: 4 }}>Departure</Th>
                            <Th px={{ base: 2, md: 4 }}>Price</Th>
                            <Th px={{ base: 2, md: 4 }}>Seats</Th>
                            <Th px={{ base: 2, md: 4 }}>Arrival</Th>
                            <Th px={{ base: 2, md: 4 }}>Actions</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {buses.map((bus, idx) => (
                            <Tr
                                key={bus._id}
                                bg={idx % 2 === 0 ? 'gray.50' : 'white'}
                                height="50px"
                            >
                                <Td px={{ base: 2, md: 4 }} fontSize={{ base: 'xs', md: 'sm' }}>
                                    {bus.company}
                                </Td>
                                <Td px={{ base: 2, md: 4 }} fontSize={{ base: 'xs', md: 'sm' }}>
                                    {bus.from}
                                </Td>
                                <Td px={{ base: 2, md: 4 }} fontSize={{ base: 'xs', md: 'sm' }}>
                                    {bus.to}
                                </Td>
                                <Td px={{ base: 2, md: 4 }} fontSize={{ base: 'xs', md: 'sm' }} color="green.600">
                                    {new Date(bus.departureDate).toLocaleString()}
                                </Td>
                                <Td px={{ base: 2, md: 4 }} fontSize={{ base: 'xs', md: 'sm' }}>
                                    {bus.price}
                                </Td>
                                <Td px={{ base: 2, md: 4 }} fontSize={{ base: 'xs', md: 'sm' }}>
                                    {bus.seatsAvailable}
                                </Td>
                                <Td px={{ base: 2, md: 4 }} fontSize={{ base: 'xs', md: 'sm' }} color="green.600">
                                    {new Date(bus.arrivalDate).toLocaleString()}
                                </Td>
                                <Td px={{ base: 2, md: 4 }}>
                                    <Button
                                        size="xs"
                                        mr={2}
                                        onClick={() => { setSelectedBus(bus); onOpen(); }}
                                    >
                                        Edit
                                    </Button>
                                    <Button
                                        size="xs"
                                        colorScheme="red"
                                        onClick={() => handleDelete(bus._id)}
                                    >
                                        Delete
                                    </Button>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>

            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{selectedBus ? 'Edit Bus' : 'Add Bus'}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <BusForm
                            initialData={selectedBus}
                            onSubmit={selectedBus ? handleUpdate : handleCreate}
                        />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default Buses;
